import React, { useState } from "react";
import { api, ROLES, MODULES } from "@/lib/api";
import { PermissionsPicker } from "@/components/PermissionsPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

const ALL_KEYS = MODULES.map((m) => m.key);

export const StaffEditDialog = ({ user, onSaved, onDeleted, size = "sm", variant = "outline", label = "Edit", showQuota = true, superMode = false }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const isAdminTarget = user.role === "admin" && !superMode;
  const roleOpts = ROLES.filter((r) => r.value !== "admin" || superMode);

  const openDialog = (o) => {
    setOpen(o);
    if (o) setForm({ name: user.name, email: user.email, role: user.role, password: "", leave_quota: user.leave_quota ?? 14, permissions: user.permissions ?? ALL_KEYS });
  };

  const save = async () => {
    try {
      const payload = { name: form.name, email: form.email };
      if (showQuota) payload.leave_quota = Number(form.leave_quota);
      if (!isAdminTarget) { payload.role = form.role; payload.permissions = form.permissions; }
      if (form.password) payload.password = form.password;
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Staff updated");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to update");
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${user.name}? Their account will be removed. Past jobs and records stay intact.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success("Staff deleted");
      setOpen(false);
      (onDeleted || onSaved) && (onDeleted || onSaved)();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <Button size={size} variant={variant} data-testid={`staff-edit-btn-${user.email}`}><Pencil className="w-3.5 h-3.5 mr-1" /> {label}</Button>
      </DialogTrigger>
      {form && (
        <DialogContent className="max-w-md" data-testid="staff-edit-dialog">
          <DialogHeader><DialogTitle>Edit {user.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-edit-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-edit-email" /></div>
            {!isAdminTarget && (
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOpts.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Reset password <span className="text-xs text-muted-foreground">(leave blank to keep)</span></Label><Input type="password" minLength="6" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="New password" data-testid="staff-edit-password" /></div>
            {showQuota && <div><Label>Annual leave quota (days)</Label><Input type="number" min="0" value={form.leave_quota} onChange={(e) => setForm({ ...form, leave_quota: e.target.value })} data-testid="staff-edit-quota" /></div>}
            {!isAdminTarget && <div><Label className="mb-1 block">Access</Label><PermissionsPicker value={form.permissions} onChange={(p) => setForm({ ...form, permissions: p })} testidPrefix="staff-edit-perm" /></div>}
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            {!isAdminTarget ? (
              <Button variant="destructive" size="sm" onClick={del} data-testid="staff-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Staff</Button>
            ) : <span className="text-xs text-muted-foreground">Admin account cannot be deleted</span>}
            <Button onClick={save} className="bg-cyan-600 hover:bg-cyan-700" data-testid="staff-edit-save">Save Changes</Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};
